import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { locales, supportedLanguages } from "./index";
import { importMessages as importCloudscapeMessages } from "@cloudscape-design/components/i18n";
import { importMessages as importPolarisMessages } from "@amzn/awsui-components-react/polaris/i18n";
// i18n configuration (shared between main and fallback)
const I18N_CONFIG = {
    fallbackLng: "en",
    defaultNS: "common",
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    returnNull: false,
};
/**
 * Load and initialize translations for a specific locale
 */
async function loadAndInitialize(locale, debug) {
    // Load resources - locale translations and UI component messages
    const [translationModule, cloudscapeMessages, polarisMessages] = await Promise.all([
        locales[locale](),
        importCloudscapeMessages(locale),
        importPolarisMessages(locale),
    ]);
    // Build resources object starting with the requested locale
    const resources = {
        [locale]: { common: translationModule.default },
    };
    // If locale is not English, also load English translations for fallback
    if (locale !== "en") {
        const englishTranslationModule = await locales.en();
        resources.en = { common: englishTranslationModule.default };
    }
    // Initialize i18n
    await i18n.use(initReactI18next).init({
        ...I18N_CONFIG,
        resources,
        lng: locale,
        debug,
    });
    // Update HTML lang
    document.documentElement.lang = locale;
    return { locale, cloudscapeMessages, polarisMessages };
}
/**
 * Bootstrap with translations, with automatic English fallback on error.
 *
 * This function orchestrates:
 * 1. Language validation and normalization
 * 2. Loading app translations, Cloudscape, and Polaris messages in parallel
 * 3. Initializing i18n
 * 4. Updating HTML lang attribute
 * 5. Calling your render callback with loaded resources
 *
 * If loading fails, automatically retries with English fallback.
 *
 * @param options - Bootstrap configuration
 * @param render - Callback to render your application
 *
 * @example
 * ```typescript
 * bootstrapWithTranslations(
 *   { locale: "es" },
 *   ({ locale, cloudscapeMessages, polarisMessages }) => {
 *     ReactDOM.createRoot(container).render(
 *       <App
 *         locale={locale}
 *         cloudscapeMessages={cloudscapeMessages}
 *         polarisMessages={polarisMessages}
 *       />
 *     );
 *   }
 * );
 * ```
 */
export function bootstrapWithTranslations(options, render) {
    const requestedLanguage = options.locale || "en";
    const normalizedLocale = supportedLanguages.includes(requestedLanguage) ? requestedLanguage : "en";
    loadAndInitialize(normalizedLocale, options.debug ?? false)
        .then(render)
        .catch((error) => {
        // Fallback to English if not already trying English
        if (normalizedLocale !== "en") {
            console.error("Failed to load translations, falling back to English:", error);
            return loadAndInitialize("en", options.debug ?? false).then(render);
        }
        // If English also fails, we're done
        console.error("Failed to load fallback translations:", error);
        throw new Error("Unable to load translations");
    });
}
